import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Smile, FileImage, Film, Mic, X, Loader2 } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';

const ChatInputToolbar = ({
  inputText,
  onInputChange,
  onSend,
  onKeyDown,
  sending,
  disabled,
  inputRef: externalRef,
  onImageSelect,
  imagePreview,
  onImageRemove,
  imageUploading,
  'data-testid-input': testIdInput = 'chat-input',
  'data-testid-send': testIdSend = 'chat-send-btn',
}) => {
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const fileInputRef = useRef(null);
  const localInputRef = useRef(null);
  const inputRefToUse = externalRef || localInputRef;

  // Click-outside to close emoji picker
  useEffect(() => {
    if (!showEmoji) return;
    const handle = (e) => {
      if (
        emojiRef.current &&
        !emojiRef.current.contains(e.target) &&
        emojiBtnRef.current &&
        !emojiBtnRef.current.contains(e.target)
      ) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showEmoji]);

  // Esc key to close emoji picker
  useEffect(() => {
    if (!showEmoji) return;
    const handle = (e) => {
      if (e.key === 'Escape') setShowEmoji(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [showEmoji]);

  const handleEmojiClick = useCallback(
    (emojiData) => {
      const emoji = emojiData.emoji;
      const input = inputRefToUse.current;
      if (input) {
        const start = input.selectionStart || inputText.length;
        const end = input.selectionEnd || inputText.length;
        const newText = inputText.slice(0, start) + emoji + inputText.slice(end);
        onInputChange(newText);
        requestAnimationFrame(() => {
          input.focus();
          input.setSelectionRange(start + emoji.length, start + emoji.length);
        });
      } else {
        onInputChange(inputText + emoji);
      }
    },
    [inputText, onInputChange, inputRefToUse],
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      alert('Only image files allowed (JPEG, PNG, WebP, GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }
    onImageSelect?.(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const PlaceholderBtn = ({ icon: Icon, label, testId }) => (
    <button
      className="p-1.5 text-zinc-600 cursor-not-allowed relative group/tool"
      title="Coming soon"
      data-testid={testId}
      disabled
      type="button"
      aria-label={`${label} - Coming soon`}
    >
      <Icon className="w-4 h-4" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-[10px] text-zinc-400 whitespace-nowrap opacity-0 group-hover/tool:opacity-100 transition-opacity pointer-events-none z-20">
        Coming soon
      </div>
    </button>
  );

  const canSend = (inputText.trim() || imagePreview) && !sending && !disabled;

  return (
    <div
      className="relative border-t border-zinc-800 bg-zinc-900/50 flex-shrink-0"
      data-testid="chat-toolbar"
    >
      {/* Emoji picker */}
      {showEmoji && (
        <div
          ref={emojiRef}
          className="absolute bottom-full left-0 mb-2 z-50"
          data-testid="emoji-picker-container"
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme="dark"
            width={300}
            height={350}
            searchPlaceholder="Search emoji..."
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            lazyLoadEmojis
          />
        </div>
      )}

      {/* Image preview strip */}
      {imagePreview && (
        <div className="px-3 pt-2 pb-1" data-testid="image-preview-strip">
          <div className="relative inline-block rounded-lg overflow-hidden border border-zinc-700 bg-zinc-800">
            {imageUploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
                <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
              </div>
            )}
            <img
              src={imagePreview}
              alt="Preview"
              className="max-h-32 max-w-[200px] object-contain"
              data-testid="image-preview-thumbnail"
            />
            <button
              onClick={onImageRemove}
              className="absolute top-1 right-1 p-0.5 bg-zinc-900/80 rounded-full hover:bg-red-600 transition-colors z-20"
              title="Remove image"
              data-testid="image-preview-remove"
              type="button"
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Toolbar row */}
      <div className="flex items-center gap-0.5 px-3 pt-2 pb-1">
        <button
          ref={emojiBtnRef}
          onClick={() => setShowEmoji((p) => !p)}
          className={`p-1.5 rounded-full transition-colors ${showEmoji ? 'text-orange-400 bg-zinc-800' : 'text-zinc-500 hover:text-orange-400'}`}
          title="Emoji"
          data-testid="emoji-btn"
          type="button"
        >
          <Smile className="w-4 h-4" />
        </button>
        <PlaceholderBtn icon={Film} label="GIFs" testId="gif-btn" />
        <button
          onClick={() => fileInputRef.current?.click()}
          className={`p-1.5 rounded-full transition-colors ${imagePreview ? 'text-orange-400 bg-zinc-800' : 'text-zinc-500 hover:text-orange-400'}`}
          title="Send image"
          data-testid="image-upload-btn"
          type="button"
          disabled={imageUploading}
        >
          <FileImage className="w-4 h-4" />
        </button>
        <PlaceholderBtn icon={Mic} label="Voice message" testId="voice-btn" />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleFileSelect}
          data-testid="image-file-input"
        />
      </div>

      {/* Input row */}
      <div className="flex items-center gap-2 px-3 pb-2.5">
        <input
          ref={inputRefToUse}
          type="text"
          value={inputText}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={imagePreview ? 'Add a caption...' : 'Type a message...'}
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
          disabled={disabled}
          data-testid={testIdInput}
        />
        <button
          onClick={onSend}
          disabled={!canSend}
          className="p-2 rounded-full bg-orange-500 text-white disabled:opacity-40 hover:bg-orange-600 transition-colors flex-shrink-0"
          data-testid={testIdSend}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ChatInputToolbar;
